import { fx } from './motion-control.js';
import { breakpoint } from '../utils/variables.js';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const ATTR_PARALLAX = 'data-parallax';
const ATTR_PARALLAX_AXIS = 'data-parallax-axis';
const ATTR_PARALLAX_OFFSET = 'data-parallax-offset';
const ATTR_PARALLAX_SCOPE = 'data-parallax-scope';
const ATTR_PARALLAX_COVER = 'data-parallax-cover';

const resolveTargets = (root) => {
    if (!root) return [];
    if (Array.isArray(root)) return root.filter((node) => node instanceof Element);
    if (root instanceof Element || root === document) {
        return Array.from(root.querySelectorAll(`[${ATTR_PARALLAX}]`));
    }
    if (typeof root.querySelectorAll === 'function') {
        return Array.from(root.querySelectorAll(`[${ATTR_PARALLAX}]`));
    }
    return [];
};

const setupParallaxFactor = (element, media, { markers = false } = {}) => {
    const wrapper = element.closest(`[${ATTR_PARALLAX_SCOPE}]`);
    if (!wrapper) return null;

    const factor = parseFloat(element.getAttribute(ATTR_PARALLAX));
    const axis = (element.getAttribute(ATTR_PARALLAX_AXIS) || 'y').toLowerCase();
    const multiplier = Number.isNaN(factor) ? 0.2 : factor;
    const breakpointKey = wrapper.getAttribute('data-parallax-breakpoint') || 'sidebar';
    const key = Object.prototype.hasOwnProperty.call(breakpoint, breakpointKey)
        ? breakpointKey
        : 'sidebar';
    const mq = `(min-width: ${breakpoint[key]}px)`;

    if (!media || typeof media.add !== 'function') return null;

    const cleanup = media.add(mq, () => {
        const range = () => window.innerHeight * multiplier;
        const fromProps = axis === 'x' ? { x: () => range() } : { y: () => range() };
        const toProps =
            axis === 'x' ? { x: () => -range(), ease: 'none' } : { y: () => -range(), ease: 'none' };

        const tween = fx.fromTo(element, fromProps, {
            ...toProps,
            scrollTrigger: {
                trigger: wrapper,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
                markers,
                onEnter: () => fx.set(element, { willChange: 'transform' }),
                onEnterBack: () => fx.set(element, { willChange: 'transform' }),
                onLeave: () => fx.set(element, { willChange: 'auto' }),
                onLeaveBack: () => fx.set(element, { willChange: 'auto' }),
                onKill: () => fx.set(element, { willChange: 'auto' }),
            },
        });

        return () => tween?.scrollTrigger?.kill();
    });

    return cleanup;
};

const setupParallaxCover = (element, options = {}) => {
    const wrapper = element.closest(`[${ATTR_PARALLAX_COVER}]`);
    if (!wrapper) return null;

    const offsetAttr = parseInt(element.getAttribute(ATTR_PARALLAX_OFFSET), 10);
    const coverOffset = Number.isNaN(offsetAttr) ? 50 : offsetAttr;

    if (getComputedStyle(wrapper).position === 'static') {
        wrapper.style.position = 'relative';
    }

    wrapper.style.overflow = 'hidden';

    const adjustCoverSize = () => {
        element.style.position = 'absolute';
        element.style.inset = `-${coverOffset}px 0`;
        element.style.height = `calc(100% + ${coverOffset * 2}px)`;
        element.style.width = '100%';
        element.style.objectFit = 'cover';
    };

    adjustCoverSize();

    const tween = fx.fromTo(
        element,
        { y: coverOffset },
        {
            y: -coverOffset,
            ease: 'none',
            scrollTrigger: {
                trigger: wrapper,
                start: 'top bottom',
                end: 'bottom top',
                scrub: true,
                invalidateOnRefresh: true,
                markers: options.markers || false,
                onRefresh: adjustCoverSize,
                onEnter: () => fx.set(element, { willChange: 'transform' }),
                onEnterBack: () => fx.set(element, { willChange: 'transform' }),
                onLeave: () => fx.set(element, { willChange: 'auto' }),
                onLeaveBack: () => fx.set(element, { willChange: 'auto' }),
                onKill: () => fx.set(element, { willChange: 'auto' }),
            },
        }
    );

    return () => tween?.scrollTrigger?.kill();
};

const setupParallax = (root, media, options = {}) => {
    const targets = resolveTargets(root);
    if (!targets.length) return [];

    const cleaners = targets.map((el) => {
        const coverCleanup = setupParallaxCover(el, options);
        const factorCleanup = setupParallaxFactor(el, media, options);
        return () => {
            coverCleanup?.();
            factorCleanup?.();
        };
    });

    return cleaners;
};

const killAllParallax = () => {
    ScrollTrigger.getAll()
        .filter((trigger) => trigger.vars?.trigger?.hasAttribute?.(ATTR_PARALLAX))
        .forEach((trigger) => trigger.kill());
};

export {
    ATTR_PARALLAX,
    ATTR_PARALLAX_AXIS,
    ATTR_PARALLAX_COVER,
    ATTR_PARALLAX_OFFSET,
    ATTR_PARALLAX_SCOPE,
    killAllParallax,
    resolveTargets,
    setupParallax,
};
export default setupParallax;
